import { Modal } from '@seiue/ui'
import {
  DomEditor,
  IDomEditor,
  IModalMenu,
  IModuleConf,
  SlateNode,
  SlateRange,
  SlateTransforms,
} from '@wangeditor/editor'

import { EditFormula } from './EditFormula'
import elemToHtmlConf from './eleToHtml'
import parseHtmlConf from './parseEleToHtml'
import withFormula from './plugin'
import renderElemConf from './renderEle'
import { FormulaElement } from './types'
import './registerEle'
import 'katex/dist/katex.min.css'

const formulaSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" fill="none" version="1.1" width="11" height="12" viewBox="0 0 11 12"><g><path d="M0,1C0,0.447717,0.447717,0,1,0C1,0,11,0,11,0C11,0,11,1.33333,11,1.33333C11,1.33333,1.67906,1.33333,1.67906,1.33333C1.67906,1.33333,6.16199,5.25591,6.16199,5.25591C6.16197,5.25589,6.16201,5.25592,6.16199,5.25591C6.60702,5.64528,6.61878,6.33367,6.18753,6.73803C6.18753,6.73803,6.11368,6.80724,6.11368,6.80724C6.11368,6.80724,1.68182,10.6667,1.68182,10.6667C1.68182,10.6667,11,10.6667,11,10.6667C11,10.6667,11,12,11,12C11,12,1,12,1,12C0.447726,12,0,11.5523,0,11C0,11,0,10.8184,0,10.8184C0,10.5292,0.125188,10.2542,0.343277,10.0643C0.343268,10.0643,0.343286,10.0643,0.343277,10.0643C0.343277,10.0643,4.99896,6.00994,4.99896,6.00994C4.99896,6.00994,0.341496,1.93465,0.341496,1.93465C0.124501,1.74477,0,1.47046,0,1.18208C0,1.18208,0,1,0,1Z" fill-rule="evenodd" fill="#455766" fill-opacity="1"/></g></svg>'

class InsertFormulaMenu implements IModalMenu {
  title: string
  tag: string
  iconSvg?: string | undefined
  ifInit: boolean = false
  showModal: boolean = false
  modalWidth: number = 300
  controlShow: (i: boolean) => void = Function.prototype as any

  constructor() {
    this.title = '插入公式'
    this.iconSvg = formulaSvg
    this.tag = 'button'
  }

  modalInit = (method: (i: boolean) => void) => {
    this.controlShow = method
  }

  isActive() {
    return false
  }

  getValue(): string | boolean {
    return ''
  }

  isDisabled(editor: IDomEditor) {
    const { selection } = editor
    if (selection == null) return true
    if (SlateRange.isExpanded(selection)) return true // 选区非折叠，禁用

    const selectedElems = DomEditor.getSelectedElems(editor)

    const hasVoidElem = selectedElems.some((elem) => editor.isVoid(elem))
    if (hasVoidElem) return true // 选中了 void 元素，禁用

    const hasPreElem = selectedElems.some(
      (elem) => DomEditor.getNodeType(elem) === 'pre'
    )
    if (hasPreElem) return true // 选中了 pre 原则，禁用

    return false
  }

  getModalPositionNode(): SlateNode | null {
    return null
  }

  getModalContentElem(): Element {
    const ele = document.documentElement
    return ele
  }

  exec(editor: IDomEditor, value: string | boolean) {
    const { hide } = Modal.open({
      header: null,
      maskStyle: {
        background: 'transparent',
      },
      bodyStyle: {
        padding: '0 0',
      },
      footer: null,
      children: (
        <EditFormula
          hide={() => hide()}
          initValue=""
          onOk={(v) => {
            editor.restoreSelection()
            const formulaElem: FormulaElement = {
              type: 'formula',
              value: v,
              children: [{ text: '' }],
            }
            editor.insertNode(formulaElem)
          }}
        />
      ),
    })
  }
}

class EditFormulaMenu extends InsertFormulaMenu {
  constructor() {
    super()
    this.title = '编辑'
    this.tag = 'button'
    this.iconSvg = ''
  }

  private getSelectedElem(editor: IDomEditor): FormulaElement | null {
    const node = DomEditor.getSelectedNodeByType(editor, 'formula')
    if (node == null) return null
    return node as FormulaElement
  }

  isDisabled(editor: IDomEditor) {
    const { selection } = editor
    if (selection == null) return true
    if (SlateRange.isExpanded(selection)) return true // 选区非折叠，禁用

    // 未匹配到 formula node 则禁用
    const formulaElem = this.getSelectedElem(editor)
    if (formulaElem == null) return true

    return false
  }

  exec(editor: IDomEditor, value: string | boolean) {
    const selectedElem = this.getSelectedElem(editor)
    if (selectedElem == null) return
    const { value: initValue } = selectedElem
    console.log('selectedElem', initValue)
    const { hide } = Modal.open({
      header: null,
      maskStyle: {
        background: 'transparent',
      },
      bodyStyle: {
        padding: '0 0',
      },
      footer: null,
      children: (
        <EditFormula
          hide={() => hide()}
          initValue={initValue}
          onOk={(v: string) => {
            editor.restoreSelection()
            if (this.isDisabled(editor)) return
            const selectedElem = this.getSelectedElem(editor)
            if (selectedElem == null) return
            const path = DomEditor.findPath(editor, selectedElem)
            const props: Partial<FormulaElement> = { value: v }
            SlateTransforms.setNodes(editor, props, { at: path })
          }}
        />
      ),
    })
  }
}

export const insertFormula = {
  key: 'insertFormula',
  factory() {
    return new InsertFormulaMenu()
  },
}

export const editFormula = {
  key: 'editFormula',
  factory() {
    return new EditFormulaMenu()
  },
}

export const formulaModule: Partial<IModuleConf> = {
  editorPlugin: withFormula,
  renderElems: [renderElemConf],
  elemsToHtml: [elemToHtmlConf],
  parseElemsHtml: [parseHtmlConf],
  menus: [insertFormula, editFormula],
}
